import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Snowflake, Plus, Check, Trash2, X, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDelete from '../../components/ConfirmDelete';

const CATEGORIES = ['Viandes', 'Poissons', 'Légumes', 'Sauces', 'Pâtisserie', 'Autre'];

const StatCard = ({ label, value, color }) => (
  <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-3 border border-[rgb(var(--lm-border)/0.3)]">
    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">{label}</p>
    <p className={`text-xl font-bold font-mono mt-1 ${color}`}>{value}</p>
  </div>
);

const PatronCongelateur = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filter, setFilter] = useState('CONGELE');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [prixSuggestions, setPrixSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [form, setForm] = useState({
    produit: '', categorie: 'Viandes', quantite: '', unite: 'kg',
    poids_kg: '', prix_kg: '', note: '', date_congelation: new Date().toISOString().split('T')[0]
  });

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, statsRes] = await Promise.all([
        api.get(`/congelateur?statut=${filter}`),
        api.get('/congelateur/stats')
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const q = form.produit.trim();
    if (q.length < 2) { setPrixSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/congelateur/prix-produit?q=${encodeURIComponent(q)}`);
        setPrixSuggestions(res.data.results || []);
        setShowSuggestions(res.data.results?.length > 0);
      } catch { setPrixSuggestions([]); }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.produit]);

  const applySuggestion = (s) => {
    setForm(f => ({ ...f, produit: s.nom, prix_kg: s.prix_unitaire, unite: s.unite || f.unite }));
    setShowSuggestions(false);
  };

  const addItem = async () => {
    if (!form.produit.trim()) { toast.error('Nom du produit requis'); return; }
    try {
      if (editingItem) {
        await api.put(`/congelateur/${editingItem.id}`, {
          produit: form.produit,
          categorie: form.categorie,
          quantite: parseFloat(form.quantite) || 0,
          unite: form.unite,
          poids_kg: parseFloat(form.poids_kg) || 0,
          prix_kg: parseFloat(form.prix_kg) || 0,
          note: form.note,
          date_congelation: form.date_congelation,
        });
        toast.success(`${form.produit} modifié`);
        setEditingItem(null);
      } else {
        const res = await api.post('/congelateur', {
          ...form,
          quantite: parseFloat(form.quantite) || 0,
          poids_kg: parseFloat(form.poids_kg) || 0,
          prix_kg: parseFloat(form.prix_kg) || 0,
        });
        if (res.data.error) { toast.error(res.data.error); return; }
        toast.success(`${form.produit} congelé`);
      }
      setForm({ produit: '', categorie: 'Viandes', quantite: '', unite: 'kg', poids_kg: '', prix_kg: '', note: '', date_congelation: new Date().toISOString().split('T')[0] });
      setShowForm(false);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (item) => {
    setForm({
      produit: item.produit || '',
      categorie: item.categorie || 'Autre',
      quantite: item.quantite || '',
      unite: item.unite || 'kg',
      poids_kg: item.poids_kg || '',
      prix_kg: item.prix_kg || '',
      note: item.note || '',
      date_congelation: item.date_congelation || new Date().toISOString().split('T')[0],
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const utiliser = async (item) => {
    try {
      const res = await api.put(`/congelateur/${item.id}/utiliser`);
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const supprimer = async (item) => {
    try {
      await api.delete(`/congelateur/${item.id}`);
      toast.success('Supprimé');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const filtered = items.filter(i =>
    !search || i.produit.toLowerCase().includes(search.toLowerCase()) ||
    i.categorie?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(i => {
    const cat = i.categorie || 'Autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });

  return (
    <div className="space-y-4 p-4 max-w-4xl mx-auto" data-testid="patron-congelateur">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center">
            <Snowflake size={20} className="text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-title text-[rgb(var(--lm-text))]">Congélateur</h1>
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Stock & valeur</p>
          </div>
        </div>
        <Button onClick={() => { setShowForm(!showForm); if (showForm) { setEditingItem(null); setForm({ produit: '', categorie: 'Viandes', quantite: '', unite: 'kg', poids_kg: '', prix_kg: '', note: '', date_congelation: new Date().toISOString().split('T')[0] }); } }}
          className={showForm ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'bg-blue-500 text-white hover:bg-blue-600'}
          data-testid="patron-add-congelateur-btn">
          {showForm ? <X size={16} /> : <><Plus size={16} className="mr-1" /> Congeler</>}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="patron-congelateur-stats">
          <StatCard label="Produits congelés" value={stats.nb_congeles} color="text-blue-400" />
          <StatCard label="Valeur stock" value={`${stats.valeur_stock.toFixed(0)}€`} color="text-[rgb(var(--lm-gold))]" />
          <StatCard label="Utilisés" value={stats.nb_utilises} color="text-green-400" />
          <StatCard label="Valeur utilisée" value={`${stats.valeur_utilisee.toFixed(0)}€`} color="text-green-400" />
        </div>
      )}

      {showForm && (
        <Card className="bg-[rgb(var(--lm-surface))] border-blue-500/20 animate-slide-up" data-testid="patron-add-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1 relative">
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Produit *</label>
                <Input value={form.produit} onChange={e => { setForm(f => ({ ...f, produit: e.target.value })); setShowSuggestions(true); }}
                  onFocus={() => prixSuggestions.length > 0 && setShowSuggestions(true)}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Ex: Saumon" autoFocus data-testid="patron-input-produit" />
                {showSuggestions && prixSuggestions.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-[rgb(var(--lm-surface-elevated))] border border-[rgb(var(--lm-border))] rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="patron-prix-suggestions">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] px-3 py-1.5 border-b border-[rgb(var(--lm-border)/0.3)] uppercase tracking-wider">Prix trouvés dans les factures</p>
                    {prixSuggestions.map((s, i) => (
                      <button key={i} onClick={() => applySuggestion(s)}
                        className="w-full text-left px-3 py-2 hover:bg-[rgb(var(--lm-gold)/0.1)] transition-colors flex items-center justify-between gap-2 border-b border-[rgb(var(--lm-border)/0.1)] last:border-0">
                        <div className="min-w-0">
                          <p className="text-xs text-[rgb(var(--lm-text))] truncate">{s.nom}</p>
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{s.fournisseur} • {s.date}</p>
                        </div>
                        <span className="text-xs font-mono font-bold text-[rgb(var(--lm-gold))] shrink-0">{s.prix_unitaire}€/{s.unite || 'kg'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Catégorie</label>
                <select value={form.categorie} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}
                  className="w-full h-10 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-sm px-3"
                  data-testid="patron-input-categorie">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Qté</label>
                <Input type="number" value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0" data-testid="patron-input-quantite" />
              </div>
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Unité</label>
                <select value={form.unite} onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}
                  className="w-full h-10 rounded-md bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-sm px-3">
                  <option value="kg">kg</option><option value="pcs">pcs</option><option value="L">L</option><option value="barq">barq</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Poids (kg)</label>
                <Input type="number" step="0.01" value={form.poids_kg} onChange={e => setForm(f => ({ ...f, poids_kg: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0.0" data-testid="patron-input-poids" />
              </div>
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Prix/kg (€)</label>
                <Input type="number" step="0.01" value={form.prix_kg} onChange={e => setForm(f => ({ ...f, prix_kg: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0.00" data-testid="patron-input-prix" />
              </div>
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Date</label>
                <Input type="date" value={form.date_congelation} onChange={e => setForm(f => ({ ...f, date_congelation: e.target.value }))}
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-xs" data-testid="patron-input-date" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Note</label>
              <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Ex: Jour de fermeture" data-testid="patron-input-note" />
            </div>
            <Button onClick={addItem} className="w-full bg-blue-500 text-white hover:bg-blue-600" data-testid="patron-submit-congelateur">
              <Snowflake size={16} className="mr-2" /> {editingItem ? 'Enregistrer' : 'Congeler'}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-[rgb(var(--lm-surface))] rounded-lg p-0.5 border border-[rgb(var(--lm-border)/0.3)]">
          {[{ key: 'CONGELE', label: 'Congelé' }, { key: 'UTILISE', label: 'Utilisé' }, { key: '', label: 'Tout' }].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded-md transition-all ${filter === f.key ? 'bg-blue-500/20 text-blue-400 font-medium' : 'text-[rgb(var(--lm-text-muted))]'}`}
              data-testid={`patron-filter-${f.key || 'all'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border)/0.3)] pl-8 h-8 text-xs" />
        </div>
      </div>

      {loading ? (
        <p className="text-center text-[rgb(var(--lm-text-muted))] text-sm py-8">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Snowflake size={40} className="mx-auto text-[rgb(var(--lm-border))] mb-3" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucun produit</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, catItems]) => (
            <div key={cat}>
              <h3 className="text-xs font-medium text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2 flex items-center gap-2">
                <span>{cat}</span>
                <span className="text-[10px] bg-[rgb(var(--lm-surface-elevated))] px-2 py-0.5 rounded-full">{catItems.length}</span>
              </h3>
              <div className="space-y-1.5">
                {catItems.map(item => {
                  const valeur = ((item.prix_kg || 0) * (item.poids_kg || 0)).toFixed(2);
                  const joursConge = item.date_congelation ? Math.floor((new Date() - new Date(item.date_congelation)) / 86400000) : 0;
                  const isOld = item.statut === 'CONGELE' && joursConge >= 7;
                  const isCritical = item.statut === 'CONGELE' && joursConge >= 14;
                  return (
                    <div key={item.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        isCritical ? 'bg-red-500/5 border-red-500/30'
                        : isOld ? 'bg-orange-500/5 border-orange-500/20'
                        : item.statut === 'CONGELE' ? 'bg-[rgb(var(--lm-surface))] border-blue-500/15 hover:border-blue-500/30'
                        : 'bg-[rgb(var(--lm-surface))] border-green-500/15 opacity-60'
                      }`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 cursor-pointer ${
                        isCritical ? 'bg-red-500/15' : isOld ? 'bg-orange-500/15' : item.statut === 'CONGELE' ? 'bg-blue-500/15' : 'bg-green-500/15'
                      }`} onClick={() => item.statut === 'CONGELE' && startEdit(item)}>
                        {isCritical ? <AlertTriangle size={14} className="text-red-400" />
                        : isOld ? <AlertTriangle size={14} className="text-orange-400" />
                        : item.statut === 'CONGELE' ? <Snowflake size={14} className="text-blue-400" />
                        : <Check size={14} className="text-green-400" />}
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => item.statut === 'CONGELE' && startEdit(item)}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[rgb(var(--lm-text))] truncate">{item.produit}</span>
                          {item.quantite > 0 && <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{item.quantite}{item.unite}</span>}
                          {isOld && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isCritical ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}`}>
                              {joursConge}j
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-[rgb(var(--lm-text-muted))]">
                          <span>{item.date_congelation}</span>
                          {item.poids_kg > 0 && <span>• {item.poids_kg}kg</span>}
                          {parseFloat(valeur) > 0 && <span className="text-[rgb(var(--lm-gold))]">• {valeur}€</span>}
                          {item.note && <span className="italic truncate max-w-[120px]">• {item.note}</span>}
                          {item.statut === 'UTILISE' && item.date_utilisation && <span className="text-green-400">• Utilisé {item.date_utilisation}</span>}
                        </div>
                      </div>
                      {item.statut === 'CONGELE' && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" onClick={() => utiliser(item)}
                            className="bg-green-500/15 text-green-400 hover:bg-green-500/25 h-8 px-2"
                            data-testid={`patron-use-${item.id}`}>
                            <Check size={14} className="mr-1" /> Utilisé
                          </Button>
                          <ConfirmDelete onConfirm={() => supprimer(item)}>
                            <Button size="sm" variant="ghost"
                              className="text-red-400/50 hover:text-red-400 h-8 px-2"
                              data-testid={`patron-del-${item.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </ConfirmDelete>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatronCongelateur;
